import {useRef} from "react";

import {useMutation} from "@tanstack/react-query";
import {httpsCallable} from "firebase/functions";
import type {ZodType, z} from "zod/v4";

import {functions} from "~/firebase/firebase";

type FunctionArgs<T extends ZodType> = {
  data: z.infer<T>;
};

export const functionHookFactoryV2 = <T extends ZodType, R extends ZodType>(
  name: string,
  type: T,
  responseSchema: R,
) => {
  const callableFunction = httpsCallable(functions, name);
  return async (data: z.infer<T>) => {
    const parsed = type.parse(data);
    const result = await callableFunction(parsed);
    const parsedResult = responseSchema.parse(result.data);
    console.log(`Function [${name}] response:`, parsedResult);
    return parsedResult;
  };
};

//TODO: replace all usages with V2 version!!!
export const functionHookFactory = <T extends ZodType>(
  name: string,
  type: T,
) => {
  const callableFunction = httpsCallable(functions, name);

  return () => {
    const isExecuting = useRef(false); // Flag to track if the function is executing

    return useMutation<void, Error, FunctionArgs<T>>({
      mutationFn: async ({data}) => {
        console.log(`Executing Function [${name}]`, JSON.stringify(data));

        if (isExecuting.current) {
          console.warn(
            `Function [${name}] is already executing. Discarding the new request.`,
          );
          return; // Discard the request
        }

        isExecuting.current = true; // Mark as executing

        try {
          const parsed = type.parse(data);
          const result = await callableFunction(parsed);
          console.log(result);
        } finally {
          isExecuting.current = false; // Reset the flag after the request completes
        }
      },
      onError: (error) => {
        console.error(`Error calling function [${name}]: `, error);
        isExecuting.current = false; // Ensure the flag is reset in case of an error
      },
    });
  };
};
